import { useMemo, useState } from 'react';

import { ProceduralEventRow } from './procedural-event-row';
import { ProceduralSourcePanel, ProceduralSourceRef } from './procedural-source-drawer';
import { ProceduralSuggestionRow } from './procedural-suggestion-row';
import type { ProceduralTimelineEventSuggestion } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { Accordion } from '@/new/components/shadcn/accordion';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Label } from '@/new/components/shadcn/label';

interface ProceduralTimelineListProps {
  events: ProceduralTimelineEvent[];
  suggestions: ProceduralTimelineEventSuggestion[];
  matterName: string;
  isExtractionInFlight: boolean;
}

interface SortKey {
  // Primary key: event date. Items without a date sort to the bottom.
  date: number;
  // Backup keys for dated ties *and* for dateless items: order by where the
  // event came from in the source document (file name asc, then page asc) so
  // undated items appear in reading order grouped by document.
  fileName: string;
  pageNumber: number;
}

type Row =
  | { kind: 'event'; sortKey: SortKey; event: ProceduralTimelineEvent }
  | { kind: 'suggestion'; sortKey: SortKey; suggestion: ProceduralTimelineEventSuggestion };

const FAR_FUTURE = Number.POSITIVE_INFINITY;

function toDateKey(date: Date | string | null | undefined): number {
  if (!date) return FAR_FUTURE;
  const ms = typeof date === 'string' ? new Date(date).getTime() : date.getTime();
  return Number.isFinite(ms) ? ms : FAR_FUTURE;
}

function suggestionEffectiveDate(s: ProceduralTimelineEventSuggestion): Date | string | null {
  if (s.kind === 'delete') return s.existingEvent?.startDateTime ?? null;
  return s.proposedStartDateTime ?? s.existingEvent?.startDateTime ?? null;
}

function eventSortKey(event: ProceduralTimelineEvent): SortKey {
  const chunk = event.sourceChunks?.[0];
  return {
    date: toDateKey(event.startDateTime),
    fileName: chunk?.file?.name ?? '',
    pageNumber: chunk?.pageNumber ?? 0,
  };
}

function suggestionSortKey(s: ProceduralTimelineEventSuggestion): SortKey {
  return {
    date: toDateKey(suggestionEffectiveDate(s)),
    fileName: s.chunk?.file?.name ?? '',
    pageNumber: s.chunk?.pageNumber ?? 0,
  };
}

function compareSortKeys(a: SortKey, b: SortKey): number {
  if (a.date !== b.date) return a.date - b.date;
  if (a.fileName !== b.fileName) return a.fileName.localeCompare(b.fileName);
  return a.pageNumber - b.pageNumber;
}

function getEditDisabledReason(isExtractionInFlight: boolean, pendingSuggestions: number): string | null {
  if (isExtractionInFlight) {
    return 'A timetable is currently being processed. Wait for it to finish before editing events.';
  }
  if (pendingSuggestions > 0) {
    return 'There are pending suggestions. Accept or reject them before editing events.';
  }
  return null;
}

export function ProceduralTimelineList({
  events,
  suggestions,
  matterName,
  isExtractionInFlight,
}: ProceduralTimelineListProps) {
  const editDisabledReason = getEditDisabledReason(isExtractionInFlight, suggestions.length);
  const [showSuggestionsChecked, setShowSuggestionsChecked] = useState(true);
  const [openSource, setOpenSource] = useState<ProceduralSourceRef | null>(null);

  // The toggle only makes sense when there are accepted events to hide the
  // suggestions against. With suggestions and no events, there'd be nothing
  // left to show, so we always render the suggestions and drop the toggle.
  const canToggleSuggestions = events.length > 0 && suggestions.length > 0;
  const showSuggestions = canToggleSuggestions ? showSuggestionsChecked : true;

  const eventsById = useMemo(() => {
    const m: Record<string, ProceduralTimelineEvent> = {};
    for (const e of events) m[e.id] = e;
    return m;
  }, [events]);

  const rows: Row[] = useMemo(() => {
    // Events with a pending update or delete suggestion are replaced by the
    // suggestion row (the suggestion already shows the post-accept view), so
    // we don't render both.
    const supersededIds = new Set(
      showSuggestions
        ? suggestions
            .filter((s) => s.kind !== 'add' && s.existingEventId !== null)
            .map((s) => s.existingEventId as string)
        : [],
    );

    const list: Row[] = events
      .filter((e) => !supersededIds.has(e.id))
      .map((event) => ({
        kind: 'event',
        sortKey: eventSortKey(event),
        event,
      }));

    if (showSuggestions) {
      for (const suggestion of suggestions) {
        list.push({
          kind: 'suggestion',
          sortKey: suggestionSortKey(suggestion),
          suggestion,
        });
      }
    }

    return list.sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
  }, [events, suggestions, showSuggestions]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {canToggleSuggestions && (
        <div className="mb-4 flex shrink-0 items-center justify-end gap-2">
          <Checkbox
            id="show-procedural-suggestions"
            checked={showSuggestions}
            onCheckedChange={(checked) => {
              setShowSuggestionsChecked(checked === true);
              // Toggling visibility can hide the row backing the open source,
              // which would leave the drawer pointing at a chunk the user
              // can no longer see in the timeline.
              setOpenSource(null);
            }}
          />
          <Label htmlFor="show-procedural-suggestions" className="mb-0 cursor-pointer text-sm text-gray-700">
            Show suggestions
          </Label>
        </div>
      )}

      <div className="flex min-h-0 flex-1 items-stretch gap-4">
        <div className="min-w-0 flex-1 overflow-y-auto pr-1">
          <Accordion type="multiple" className="flex flex-col gap-3 pl-3">
            {rows.map((row) => (
              <div
                key={row.kind === 'event' ? `event-${row.event.id}` : `suggestion-${row.suggestion.id}`}
                className="group relative w-full pl-10"
              >
                <div className="absolute top-1/2 left-0.25 z-10 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-teal-500 bg-white" />
                <div className="absolute inset-0 -inset-y-3 w-0.5 translate-x-[8px] bg-gray-200 group-first:top-1/2 group-last:bottom-1/2"></div>

                {row.kind === 'event' ? (
                  <ProceduralEventRow
                    event={row.event}
                    eventsById={eventsById}
                    matterName={matterName}
                    onOpenSource={setOpenSource}
                    editDisabledReason={editDisabledReason}
                  />
                ) : (
                  <ProceduralSuggestionRow
                    suggestion={row.suggestion}
                    eventsById={eventsById}
                    onOpenSource={setOpenSource}
                  />
                )}
              </div>
            ))}
          </Accordion>
        </div>

        <ProceduralSourcePanel source={openSource} onClose={() => setOpenSource(null)} />
      </div>
    </div>
  );
}
